import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';

export function GeocodingStatus({
  totalAddresses, geocodingPending, geocodingActive, geocodingSticky,
  confirmedCount, approximateCount,
  showApproximate, setShowApproximate,
  isFiltered, filteredCount, totalCount,
  refreshing, handleRefresh,
  compact = false,
}) {
  const showProgress = totalAddresses > 0 && (geocodingPending > 0 || geocodingActive || geocodingSticky);
  const rawPercent = totalAddresses > 0 ? Math.round(((confirmedCount + approximateCount) / totalAddresses) * 100) : 0;

  // Monotonic-when-stable: when totalAddresses is unchanged tick-to-tick,
  // the raw percent shouldn't move backwards — that's just two endpoints
  // (status poll + cluster fetch) racing each other. Clamp upward in that
  // case. But when totalAddresses *changes* (progressive batch load,
  // dataset switch, geocoding finishing), accept the new value as
  // authoritative even if lower — otherwise the display locks at a
  // spurious peak from a partial-load snapshot.
  const [displayPercent, setDisplayPercent] = useState(rawPercent);
  const lastTotalRef = useRef(totalAddresses);
  useEffect(() => {
    const prevTotal = lastTotalRef.current;
    lastTotalRef.current = totalAddresses;
    if (totalAddresses === prevTotal) {
      setDisplayPercent(prev => (rawPercent > prev ? rawPercent : prev));
    } else {
      setDisplayPercent(rawPercent);
    }
  }, [rawPercent, totalAddresses]);

  // When geocoding finishes (pending hits 0) snap to the real value so we
  // don't sit at 99% forever due to integer rounding noise.
  useEffect(() => {
    if (geocodingPending === 0 && totalAddresses > 0) {
      setDisplayPercent(rawPercent);
    }
  }, [geocodingPending, totalAddresses, rawPercent]);

  const geocodedPercent = displayPercent;

  return (
    <div className={compact ? 'px-3 py-0.5 flex items-center justify-between gap-3' : 'px-4 py-1.5 flex items-center justify-between gap-3'}>
      <div className="flex-1 flex items-center gap-3" data-testid="geocoding-status">
        {showProgress ? (
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">{confirmedCount.toLocaleString()}</span> street-level
                {approximateCount > 0 && (
                  <> &middot; <span className="font-medium text-foreground">{approximateCount.toLocaleString()}</span> approximate (ZIP)</>
                )}
                {geocodingPending > 0 && (
                  <> &middot; <span className="text-amber-600 font-medium">{geocodingPending.toLocaleString()}</span> pending</>
                )}
              </span>
              <span
                className={
                  geocodingPending > 0
                    ? 'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 font-medium whitespace-nowrap'
                    : 'text-muted-foreground'
                }
                data-testid="geocoding-progress-banner"
              >
                {geocodingPending > 0 ? (
                  <>
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    {geocodedPercent}% &middot;{' '}
                    {(() => {
                      const mins = Math.ceil(geocodingPending * 1.1 / 60);
                      if (mins >= 60) return `~${Math.floor(mins/60)}h ${mins%60}m remaining`;
                      return `~${mins} min remaining`;
                    })()}
                    {' '}<span className="italic font-normal opacity-80">(initial load &middot; cached after)</span>
                  </>
                ) : (
                  <>{geocodedPercent}% &middot; Completing...</>
                )}
              </span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500 flex">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${totalAddresses > 0 ? Math.round((confirmedCount / totalAddresses) * 100) : 0}%` }}
                />
                <div
                  className="h-full bg-gray-400"
                  style={{ width: `${totalAddresses > 0 ? Math.round((approximateCount / totalAddresses) * 100) : 0}%` }}
                />
              </div>
            </div>
            <div className={compact ? 'hidden' : 'flex items-center gap-3 mt-1'}>
              <p className="text-[10px] text-muted-foreground flex-1">
                <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full mr-1 align-middle" />Street-level (shown)
                {approximateCount > 0 && (
                  <span className="ml-2"><span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-1 align-middle" />Approximate (ZIP center)</span>
                )}
              </p>
              {approximateCount > 0 && (
                <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer select-none" data-testid="show-approximate-toggle">
                  <input
                    type="checkbox"
                    checked={showApproximate}
                    onChange={(e) => setShowApproximate(e.target.checked)}
                    className="h-3 w-3 rounded accent-gray-500"
                  />
                  Show approximate pins
                </label>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {totalAddresses > 0 ? (
              <>
                <span>
                  <span className="font-medium text-foreground">{confirmedCount.toLocaleString()}</span> street-level
                  {approximateCount > 0 && (
                    <> &middot; <span className="font-medium text-foreground">{approximateCount.toLocaleString()}</span> approximate</>
                  )}
                </span>
                {approximateCount > 0 && (
                  <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="show-approximate-toggle">
                    <input
                      type="checkbox"
                      checked={showApproximate}
                      onChange={(e) => setShowApproximate(e.target.checked)}
                      className="h-3 w-3 rounded accent-gray-500"
                    />
                    Show approximate
                  </label>
                )}
                {isFiltered && (
                  <span className="text-amber-600">({filteredCount.toLocaleString()} shown)</span>
                )}
              </>
            ) : (
              <span className="text-xs text-muted-foreground">Loading geocoding data...</span>
            )}
          </div>
        )}
        <Button
          variant="outline" size="sm"
          className="h-7 text-xs px-3 shrink-0"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-map-btn"
        >
          {refreshing ? 'Refreshing...' : 'Refresh Map'}
        </Button>
      </div>
    </div>
  );
}
